import React, { useState, useEffect } from 'react';
import { Box, Button, SvgIcon, Tooltip, IconButton } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { spaces, palette, borderRadius } from '../../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../../utils/getMuiIcon';
import LabelledTextField from '../../../LabelledTextField/LabelledTextField';
import { v4 as uuidv4 } from 'uuid';

type LinkItem = {
  value: string;
  id: string;
};

interface AIAddLinksProps {
  links: string[];
  setLinks: (links: string[]) => void;
  maxLinks?: number;
}

export default function AIAddLinks({ links, setLinks, maxLinks = 3 }: AIAddLinksProps) {
  const { t } = useTranslation();
  const [linkItems, setLinkItems] = useState<LinkItem[]>(() => 
    links.map((link) => ({ value: link, id: uuidv4() }))
  );

  useEffect(() => {
    setLinks(linkItems.map(item => item.value));
  }, [linkItems, setLinks]);

  const handleAddLinkButtonClick = () => {
    if (linkItems.length < maxLinks) {
      setLinkItems([...linkItems, { value: '', id: uuidv4() }]);
    }
  };

  const handleChange = (index: number, value: string) => {
    setLinkItems(linkItems.map((item, linkIndex) => 
      linkIndex === index ? { ...item, value } : item
    ));
  };

  const handleRemoveLink = (index: number) => {
    setLinkItems(linkItems.filter((_, linkIndex) => linkIndex !== index));
  };

  const isLimitReached = linkItems.length >= maxLinks;

  return (
    <Grid item xs={12} sx={{ pl: spaces.xs, mt: spaces.xs }}>
      <Tooltip
        title={isLimitReached ? t('You have reached the maximum number of links') : ''}
        arrow
        placement='left'
      >
        <span data-testid='ai-assistant-add-link-button-wrapper'>
          <Button
            data-testid='ai-assistant-add-link-button'
            variant='outlined'
            onClick={handleAddLinkButtonClick}
            disabled={isLimitReached}
            fullWidth
            aria-label={isLimitReached ? t('Maximum number of links reached') : t('Add link')}
            sx={{
              mb: spaces.xs,
              border: `2px solid ${palette.components.input.outlined.enabledBorder}`,
              borderRadius: borderRadius.sm,
              color: palette.common.black,
              backgroundColor: palette.background.paperElevation1,
              '&:hover': {
                border: `2px solid ${palette.components.input.outlined.enabledBorder}`,
                boxShadow: `inset 0 0 0 1px ${palette.common.black}`,
                backgroundColor: palette.background.paperElevation0,
              },
            }}
            startIcon={
              <SvgIcon 
                component={getMuiIcon(isLimitReached ? 'disabledAddLink' : 'addLink')}
                aria-hidden='true'
              />
            }
          >
            {t('Link Button')}
          </Button>
        </span>
      </Tooltip>

      {linkItems.map((linkItem, index) => (
        <Box 
          key={linkItem.id} 
          sx={{ 
            mb: spaces.xs,
            display: 'flex',
            alignItems: 'center',
            gap: spaces.xs
          }}
        >
          <Box sx={{ flex: 1 }}>
            <LabelledTextField
              dataTestId='ai-assistant-link-field'
              placeholder={t('Link Field Placeholder')}
              name={`link${index}`}
              value={linkItem.value}
              onChange={(e) => handleChange(index, e.target.value)}
            />
          </Box>
          <IconButton
            data-testid={`ai-assistant-remove-link-button-${index}`}
            onClick={() => handleRemoveLink(index)}
            size='medium'
            aria-label={t('Remove link')}
            sx={{
              mt: spaces.xxs,
              color: palette.text.secondary,
              '&:hover': {
                // Light error background for hover state to indicate destructive action
                backgroundColor: alpha(palette.error.main, 0.08),
                color: palette.error.main
              }
            }}
          >
            <SvgIcon
              sx={{ width: 24, height: 24 }}
              viewBox='0 0 24 24'
              component={getMuiIcon('trash')}
            />
          </IconButton>
        </Box>
      ))}
    </Grid>
  );
}
